import React, { useEffect, useMemo, useState } from "react"
import { ui } from "../../../shared/styles/uiSystem"
import SectionCard from "../../../shared/components/SectionCard"
import StatusBanner from "../../../shared/components/StatusBanner"
import EmptyStateBlock from "../../../shared/components/EmptyStateBlock"
import StatusPill from "../../../shared/components/StatusPill"

export default function PatientFinanceTab({
  isReadOnly,
  readOnlyReason,
  formatDateTime,

  patientFinance,
  loadingFinance,

  openCredits,
  pendingReceivables,
  loadingAdvancedFinance,

  patientAllocations,
  reversingAllocation,
  reverseAllocation,

  cancellingPayment,
  cancelPayment,
  printPaymentReceipt,

  financialTimeline,
  loadingFinancialTimeline,

  paymentsRef,
  receivablesRef,
  manualApplyRef,
  timelineFinanceRef,

  newPaymentForm,
  setNewPaymentForm,
  registerNewPayment,
  savingPayment,
  savingAndPrinting,

  manualReceivableForm,
  setManualReceivableForm,
  createManualReceivable,
  savingManualReceivable,

  manualApplyForm,
  setManualApplyForm,
  applyPaymentManually,
  applyingPayment
}) {
  const [manualReceivableOpen, setManualReceivableOpen] = useState(false)

  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440
  )

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  const compact = viewportWidth < 980
  const veryCompact = viewportWidth < 720

  const financeOverview = useMemo(() => {
    const produced = Number(patientFinance?.produced || 0)
    const paid = Number(patientFinance?.paid || 0)
    const credit = Number(patientFinance?.credit || 0)

    const pendingFromFinance =
      Number(patientFinance?.pending || 0) > 0
        ? Number(patientFinance?.pending || 0)
        : Number(patientFinance?.totalReceivable || 0)

    const pendingFromReceivables = Array.isArray(pendingReceivables)
      ? pendingReceivables.reduce((sum, item) => sum + Number(item?.balance || 0), 0)
      : 0

    const pending =
      pendingFromFinance > 0 ? pendingFromFinance : pendingFromReceivables

    return {
      produced,
      paid,
      credit,
      pending
    }
  }, [patientFinance, pendingReceivables])

  const displayFinancialTimeline = useMemo(() => {
    const rows = Array.isArray(financialTimeline) ? financialTimeline : []

    const hiddenTypes = new Set([
      "PAYMENT_APPLIED",
      "CREDIT_AUTO_APPLIED"
    ])

    return rows.filter((item) => {
      const type = String(item?.type || "").trim().toUpperCase()
      const title = String(item?.title || "").trim().toLowerCase()
      const subtitle = String(item?.subtitle || "").trim().toLowerCase()

      if (hiddenTypes.has(type)) {
        return false
      }

      const looksLikeApplyNoise =
        title.includes("pago aplicado") ||
        subtitle.includes("pago aplicado") ||
        title.includes("saldo aplicado") ||
        subtitle.includes("saldo aplicado") ||
        title.includes("aplicación automática") ||
        subtitle.includes("aplicación automática") ||
        title.includes("aplicado automáticamente") ||
        subtitle.includes("aplicado automáticamente")

      if (type !== "PAYMENT_REGISTERED" && looksLikeApplyNoise) {
        return false
      }

      return true
    })
  }, [financialTimeline])

  const shouldShowFinancialTimeline =
    loadingFinancialTimeline || displayFinancialTimeline.length > 0

  const payments = Array.isArray(patientFinance?.payments) ? patientFinance.payments : []
  const receivables = Array.isArray(patientFinance?.receivables) ? patientFinance.receivables : []
  const openCreditsRows = Array.isArray(openCredits) ? openCredits : []
  const pendingReceivablesRows = Array.isArray(pendingReceivables) ? pendingReceivables : []
  const allocationRows = Array.isArray(patientAllocations) ? patientAllocations : []

  const hasPendingReceivables = pendingReceivablesRows.length > 0
  const hasOpenCreditRows = openCreditsRows.length > 0
  const canUseManualApply = hasOpenCreditRows && hasPendingReceivables && !isReadOnly
  const canUseQuickTargetReceivable = hasPendingReceivables && !isReadOnly

  const summaryNarrative = buildFinanceNarrative({
    overview: financeOverview,
    openCreditsRows,
    pendingReceivablesRows
  })

  const updateManualReceivableForm = (field, value) => {
    if (!setManualReceivableForm) return

    setManualReceivableForm((prev) => ({
      ...(prev || {}),
      [field]: value
    }))
  }

  const submitManualReceivable = async () => {
    if (!createManualReceivable) return

    const ok = await createManualReceivable()

    if (ok) {
      setManualReceivableOpen(false)
    }
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <SectionCard
        title="Estado financiero"
        subtitle="Lectura rápida del estado de cuenta del paciente"
      >
        {loadingFinance ? (
          <EmptyStateBlock
            title="Cargando estado financiero"
            text="Estamos preparando la lectura financiera del paciente."
            compact
          />
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: compact
                  ? "repeat(2, minmax(0,1fr))"
                  : "repeat(4, minmax(0,1fr))",
                gap: 12
              }}
            >
              <FinanceMetricCard
                title="Producción"
                value={`${formatFinanceMoney(financeOverview.produced)}`}
              />
              <FinanceMetricCard
                title="Pagado"
                value={`${formatFinanceMoney(financeOverview.paid)}`}
                tone="success"
              />
              <FinanceMetricCard
                title="Saldo a favor"
                value={`${formatFinanceMoney(financeOverview.credit)}`}
                tone="info"
              />
              <FinanceMetricCard
                title="Pendiente"
                value={`${formatFinanceMoney(financeOverview.pending)}`}
                tone="danger"
              />
            </div>

            <NarrativeBanner text={summaryNarrative} />
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Agregar deuda al paciente"
        subtitle="Registra saldos anteriores o cargos no ligados a una cita"
      >
        {isReadOnly ? (
          <StatusBanner
            tone="warning"
            title="Modo solo lectura"
            text={readOnlyReason || "No tienes permisos para agregar deudas."}
          />
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: viewportWidth <= 720 ? "1fr" : "1.4fr 0.8fr auto",
                gap: 10,
                alignItems: "end"
              }}
            >
              <div>
                <label style={ui.label}>Concepto</label>
                <input
                  value={manualReceivableForm?.concept || ""}
                  onChange={(e) => updateManualReceivableForm("concept", e.target.value)}
                  placeholder="Saldo anterior"
                  style={ui.input}
                />
              </div>

              <div>
                <label style={ui.label}>Monto</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={manualReceivableForm?.totalAmount || ""}
                  onChange={(e) => updateManualReceivableForm("totalAmount", e.target.value)}
                  placeholder="0.00"
                  style={ui.input}
                />
              </div>

              <ActionButton
                tone="warning"
                disabled={savingManualReceivable || isReadOnly}
                onClick={() => setManualReceivableOpen((prev) => !prev)}
              >
                {manualReceivableOpen ? "Ocultar detalle" : "Agregar deuda"}
              </ActionButton>
            </div>

            {manualReceivableOpen ? (
              <div
                style={{
                  border: "1px solid #fed7aa",
                  background: "#fff7ed",
                  borderRadius: 18,
                  padding: 14,
                  display: "grid",
                  gap: 12
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: viewportWidth <= 720 ? "1fr" : "220px 1fr",
                    gap: 10
                  }}
                >
                  <div>
                    <label style={ui.label}>Vencimiento opcional</label>
                    <input
                      type="date"
                      value={manualReceivableForm?.dueDate || ""}
                      onChange={(e) => updateManualReceivableForm("dueDate", e.target.value)}
                      style={ui.input}
                    />
                  </div>

                  <div>
                    <label style={ui.label}>Notas</label>
                    <input
                      value={manualReceivableForm?.notes || ""}
                      onChange={(e) => updateManualReceivableForm("notes", e.target.value)}
                      placeholder="Ej. Adeudo previo antes de usar el sistema"
                      style={ui.input}
                    />
                  </div>
                </div>

                <InfoCallout
                  title="Deuda manual"
                  text="Se guardará como cuenta por cobrar del paciente. Después podrás aplicar pagos igual que con cualquier saldo pendiente."
                />

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
                  <ActionButton
                    tone="neutral"
                    disabled={savingManualReceivable}
                    onClick={() => setManualReceivableOpen(false)}
                  >
                    Cancelar
                  </ActionButton>

                  <ActionButton
                    tone="success"
                    disabled={savingManualReceivable || isReadOnly}
                    onClick={submitManualReceivable}
                  >
                    {savingManualReceivable ? "Guardando..." : "Guardar deuda"}
                  </ActionButton>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Cobro rápido"
        subtitle="Registra cobros y aplica saldo a cartera pendiente cuando corresponda"
      >
        {isReadOnly ? (
          <StatusBanner
            tone="danger"
            title="Finanzas en solo lectura"
            text={readOnlyReason}
            style={{ marginBottom: 12 }}
          />
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: compact ? "1fr" : "minmax(0, 1.1fr) minmax(280px, 0.9fr)",
            gap: 16,
            alignItems: "start"
          }}
        >
          <div style={panelBoxStyle}>
            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <StatusPill tone="neutral">
                  Cuentas pendientes: {pendingReceivablesRows.length}
                </StatusPill>
                <StatusPill tone="info">
                  Pagos con saldo: {openCreditsRows.length}
                </StatusPill>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: veryCompact ? "1fr" : "repeat(2, minmax(0,1fr))",
                  gap: 12
                }}
              >
                <div>
                  <label style={ui.label}>Monto</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={newPaymentForm.amount}
                    onChange={(e) =>
                      setNewPaymentForm((prev) => ({
                        ...prev,
                        amount: e.target.value
                      }))
                    }
                    style={ui.input}
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <label style={ui.label}>Método</label>
                  <select
                    value={newPaymentForm.method}
                    onChange={(e) =>
                      setNewPaymentForm((prev) => ({
                        ...prev,
                        method: e.target.value
                      }))
                    }
                    style={ui.select}
                    disabled={isReadOnly}
                  >
                    <option value="CASH">Efectivo</option>
                    <option value="CARD">Tarjeta</option>
                    <option value="TRANSFER">Transferencia</option>
                    <option value="MIXED">Mixto</option>
                    <option value="OTHER">Otro</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={ui.label}>Notas</label>
                <textarea
                  rows={3}
                  placeholder="Notas del pago"
                  value={newPaymentForm.notes}
                  onChange={(e) =>
                    setNewPaymentForm((prev) => ({
                      ...prev,
                      notes: e.target.value
                    }))
                  }
                  style={ui.textarea}
                  disabled={isReadOnly}
                />
              </div>

              <InfoCallout
                title={hasPendingReceivables ? "Aplicación de pago" : "Sin cartera pendiente"}
                text={
                  hasPendingReceivables
                    ? "Puedes seleccionar una cuenta específica. Si el cobro viene desde una cita, OdontoFlow ya intenta aplicar el pago al pendiente de esa atención."
                    : "Este paciente no tiene cuentas por cobrar abiertas. Si registras un pago, quedará como saldo a favor disponible."
                }
              />

              <div style={softInnerBoxStyle}>
                <label style={checkboxRowStyle}>
                  <input
                    type="checkbox"
                    checked={newPaymentForm.autoAllocate}
                    onChange={(e) =>
                      setNewPaymentForm((prev) => ({
                        ...prev,
                        autoAllocate: e.target.checked,
                        accountReceivableId: e.target.checked
                          ? prev.accountReceivableId
                          : ""
                      }))
                    }
                    disabled={!canUseQuickTargetReceivable}
                  />
                  Aplicar este pago a una cuenta por cobrar específica
                </label>

                {newPaymentForm.autoAllocate ? (
                  <div>
                    <label style={ui.label}>Cuenta por cobrar destino</label>
                    <select
                      value={newPaymentForm.accountReceivableId}
                      onChange={(e) =>
                        setNewPaymentForm((prev) => ({
                          ...prev,
                          accountReceivableId: e.target.value
                        }))
                      }
                      style={ui.select}
                      disabled={!canUseQuickTargetReceivable}
                    >
                      <option value="">Selecciona una cuenta</option>
                      {pendingReceivablesRows.map((item) => (
                        <option key={item.id} value={item.id}>
                          {(item.concept || "Cuenta por cobrar")} · Saldo {formatFinanceMoney(item.balance)}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  flexWrap: "wrap"
                }}
              >
                <ActionButton
                  tone="info"
                  disabled={savingPayment || savingAndPrinting || isReadOnly}
                  onClick={() => registerNewPayment({ shouldPrint: false })}
                >
                  {savingPayment ? "Registrando..." : "Registrar pago"}
                </ActionButton>

                <ActionButton
                  tone="success"
                  disabled={savingPayment || savingAndPrinting || isReadOnly}
                  onClick={() => registerNewPayment({ shouldPrint: true })}
                >
                  {savingAndPrinting ? "Registrando e imprimiendo..." : "Registrar e imprimir"}
                </ActionButton>
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gap: 12 }}>
            <SubsectionPanel
              title="Situación actual"
              subtitle="Contexto rápido para cobrar mejor"
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0,1fr))",
                  gap: 10
                }}
              >
                <CompactMetricTile
                  label="Pendiente"
                  value={`${formatFinanceMoney(financeOverview.pending)}`}
                />
                <CompactMetricTile
                  label="Saldo a favor"
                  value={`${formatFinanceMoney(financeOverview.credit)}`}
                />
                <CompactMetricTile
                  label="Pagos"
                  value={formatCount(payments.length)}
                />
                <CompactMetricTile
                  label="CxC pendientes"
                  value={formatCount(pendingReceivablesRows.length)}
                />
              </div>
            </SubsectionPanel>

            <SubsectionPanel
              title="Pendientes más inmediatos"
              subtitle="Lo que conviene vigilar antes de cobrar"
            >
              {pendingReceivablesRows.length ? (
                <div style={{ display: "grid", gap: 8 }}>
                  {pendingReceivablesRows.slice(0, 3).map((item) => (
                    <MiniLedgerRow
                      key={item.id}
                      title={item.concept || "Cuenta por cobrar"}
                      subtitle={`Saldo ${formatFinanceMoney(item.balance || 0)}`}
                      meta={formatDateTime(item.createdAt)}
                      rightContent={
                        <StatusPill tone={receivableStatusTone(item.status)}>
                          {translateReceivableStatus(item.status)}
                        </StatusPill>
                      }
                    />
                  ))}
                </div>
              ) : (
                <EmptyStateBlock
                  title="Sin pendientes inmediatos"
                  text="No hay cuentas por cobrar abiertas para este paciente."
                  compact
                />
              )}
            </SubsectionPanel>
          </div>
        </div>
      </SectionCard>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: compact ? "1fr" : "minmax(0, 1fr) minmax(0, 1fr)",
          gap: 16,
          alignItems: "start"
        }}
      >
        <div ref={paymentsRef}>
          <SectionCard
            title="Pagos registrados"
            subtitle="Movimientos cobrados al paciente"
          >
            {!payments.length ? (
              <EmptyStateBlock
                title="Sin pagos registrados"
                text="Todavía no hay movimientos cobrados para este paciente."
                compact
              />
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {payments.map((item) => (
                  <LedgerCard
                    key={item.id}
                    title={`${formatFinanceMoney(item.amount || 0)}`}
                    eyebrow={translatePaymentMethod(item.method)}
                    subtitle={`${formatDateTime(item.createdAt)} · ${translateReferenceType(item.referenceType)}`}
                    meta={
                      item.unallocatedAmount > 0
                        ? `Saldo a favor: ${formatFinanceMoney(item.unallocatedAmount || 0)}`
                        : "Sin saldo pendiente por aplicar"
                    }
                    status={
                      <StatusPill tone={paymentStatusTone(item.status)}>
                        {translatePaymentStatus(item.status)}
                      </StatusPill>
                    }
                    actions={
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <MiniActionButton
                          tone="info"
                          onClick={() => printPaymentReceipt(item.id)}
                        >
                          Recibo
                        </MiniActionButton>

                        {String(item.status || "").toUpperCase() === "CANCELLED" ? (
                          <StatusPill tone="neutral">Cancelado</StatusPill>
                        ) : (
                          <MiniActionButton
                            tone="danger"
                            disabled={cancellingPayment || isReadOnly}
                            onClick={() => cancelPayment(item.id)}
                          >
                            Cancelar
                          </MiniActionButton>
                        )}
                      </div>
                    }
                  />
                ))}
              </div>
            )}
          </SectionCard>
        </div>

        <div ref={receivablesRef}>
          <SectionCard
            title="Cuentas por cobrar"
            subtitle="Pendientes ligados a tratamientos o producción"
          >
            {!receivables.length ? (
              <EmptyStateBlock
                title="Sin cuentas por cobrar"
                text="No hay pendientes financieros ligados a este paciente."
                compact
              />
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {receivables.map((item) => (
                  <LedgerCard
                    key={item.id}
                    title={item.concept || "Cuenta por cobrar"}
                    eyebrow={`Saldo ${formatFinanceMoney(item.balance || 0)}`}
                    subtitle={`${formatDateTime(item.createdAt)} · Total ${formatFinanceMoney(item.totalAmount || 0)}`}
                    meta={`Abonado ${formatFinanceMoney(item.paidAmount || 0)}`}
                    status={
                      <StatusPill tone={receivableStatusTone(item.status)}>
                        {translateReceivableStatus(item.status)}
                      </StatusPill>
                    }
                  />
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </div>

      <SectionCard
        title="Cobranza avanzada"
        subtitle="Aplicación de saldos y recuperación de cartera"
      >
        {loadingAdvancedFinance ? (
          <EmptyStateBlock
            title="Cargando cobranza avanzada"
            text="Estamos preparando la vista de recuperación y aplicación de saldos."
            compact
          />
        ) : (
          <div style={{ display: "grid", gap: 16 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: compact
                  ? "repeat(2, minmax(0,1fr))"
                  : "repeat(4, minmax(0,1fr))",
                gap: 12
              }}
            >
              <FinanceMetricCard
                title="Pagos con saldo"
                value={formatCount(openCreditsRows.length)}
              />
              <FinanceMetricCard
                title="CxC recuperables"
                value={formatCount(pendingReceivablesRows.length)}
              />
              <FinanceMetricCard
                title="Asignaciones"
                value={formatCount(allocationRows.length)}
              />
              <FinanceMetricCard
                title="Pendiente recuperable"
                value={`${formatFinanceMoney(
                  pendingReceivablesRows.reduce((sum, item) => sum + Number(item?.balance || 0), 0)
                )}`}
                tone="danger"
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: compact ? "1fr" : "minmax(0, 1fr) minmax(0, 1fr)",
                gap: 16,
                alignItems: "start"
              }}
            >
              <SubsectionPanel
                title="Pagos con saldo disponible"
                subtitle="Recursos listos para aplicar"
              >
                {openCreditsRows.length ? (
                  <div style={{ display: "grid", gap: 8 }}>
                    {openCreditsRows.map((item) => (
                      <MiniLedgerRow
                        key={item.id}
                        title={`${translatePaymentMethod(item.method)} · ${formatFinanceMoney(item.amount || 0)}`}
                        subtitle={`Disponible ${formatFinanceMoney(item.unallocatedAmount || 0)}`}
                        meta={formatDateTime(item.createdAt)}
                        rightContent={
                          <StatusPill tone="info">
                            {translateReferenceType(item.referenceType)}
                          </StatusPill>
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyStateBlock
                    title="Sin pagos con saldo disponible"
                    text="Aquí aparecerán los pagos que todavía tienen saldo libre para aplicar."
                    compact
                  />
                )}
              </SubsectionPanel>

              <SubsectionPanel
                title="Cuentas por cobrar pendientes"
                subtitle="Cartera recuperable del paciente"
              >
                {pendingReceivablesRows.length ? (
                  <div style={{ display: "grid", gap: 8 }}>
                    {pendingReceivablesRows.map((item) => (
                      <MiniLedgerRow
                        key={item.id}
                        title={item.concept || "Cuenta por cobrar"}
                        subtitle={`Saldo ${formatFinanceMoney(item.balance || 0)}`}
                        meta={formatDateTime(item.createdAt)}
                        rightContent={
                          <StatusPill tone={receivableStatusTone(item.status)}>
                            {translateReceivableStatus(item.status)}
                          </StatusPill>
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyStateBlock
                    title="Sin cuentas por cobrar pendientes"
                    text="No hay cartera pendiente disponible para recuperación."
                    compact
                  />
                )}
              </SubsectionPanel>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: compact ? "1fr" : "minmax(0, 0.95fr) minmax(0, 1.05fr)",
                gap: 16,
                alignItems: "start"
              }}
            >
              <div ref={manualApplyRef}>
                <SubsectionPanel
                  title="Aplicar saldo manualmente"
                  subtitle="Asigna saldo disponible a cuentas pendientes"
                >
                  {isReadOnly ? (
                    <StatusBanner
                      tone="danger"
                      title="Cobranza avanzada en solo lectura"
                      text={readOnlyReason}
                      style={{ marginBottom: 12 }}
                    />
                  ) : null}

                  {!canUseManualApply ? (
                    <InfoCallout
                      title="Aplicación manual no disponible"
                      text={
                        !hasOpenCreditRows && !hasPendingReceivables
                          ? "No hay pagos con saldo libre ni cuentas pendientes para cruzar."
                          : !hasOpenCreditRows
                            ? "No hay pagos con saldo disponible. Cuando exista un excedente, aparecerá aquí."
                            : "No hay cuentas por cobrar pendientes para aplicar saldo."
                      }
                    />
                  ) : null}

                  <div style={{ display: "grid", gap: 12 }}>
                    <div>
                      <label style={ui.label}>Pago con saldo disponible</label>
                      <select
                        value={manualApplyForm.paymentId}
                        onChange={(e) =>
                          setManualApplyForm((prev) => ({
                            ...prev,
                            paymentId: e.target.value
                          }))
                        }
                        style={ui.select}
                        disabled={!canUseManualApply}
                      >
                        <option value="">Selecciona un pago</option>
                        {openCreditsRows.map((item) => (
                          <option key={item.id} value={item.id}>
                            {formatDateTime(item.createdAt)} · {translatePaymentMethod(item.method)} · Disponible {formatFinanceMoney(item.unallocatedAmount)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={ui.label}>Cuenta por cobrar pendiente</label>
                      <select
                        value={manualApplyForm.accountReceivableId}
                        onChange={(e) =>
                          setManualApplyForm((prev) => ({
                            ...prev,
                            accountReceivableId: e.target.value
                          }))
                        }
                        style={ui.select}
                        disabled={!canUseManualApply}
                      >
                        <option value="">Selecciona una cuenta</option>
                        {pendingReceivablesRows.map((item) => (
                          <option key={item.id} value={item.id}>
                            {(item.concept || "Cuenta por cobrar")} · Saldo {formatFinanceMoney(item.balance)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={ui.label}>Monto a aplicar (opcional)</label>
                      <input
                        type="number"
                        placeholder="Si lo dejas vacío, aplica lo máximo posible"
                        value={manualApplyForm.amount}
                        onChange={(e) =>
                          setManualApplyForm((prev) => ({
                            ...prev,
                            amount: e.target.value
                          }))
                        }
                        style={ui.input}
                        disabled={!canUseManualApply}
                      />
                    </div>

                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <ActionButton
                        tone="success"
                        disabled={applyingPayment || !canUseManualApply}
                        onClick={applyPaymentManually}
                      >
                        {applyingPayment ? "Aplicando..." : "Aplicar saldo"}
                      </ActionButton>
                    </div>
                  </div>
                </SubsectionPanel>
              </div>

              <SubsectionPanel
                title="Asignaciones realizadas"
                subtitle="Aplicaciones históricas de pagos a cuentas"
              >
                {!allocationRows.length ? (
                  <EmptyStateBlock
                    title="Sin asignaciones registradas"
                    text="Todavía no hay aplicaciones históricas de pagos a cuentas por cobrar."
                    compact
                  />
                ) : (
                  <div style={{ display: "grid", gap: 8 }}>
                    {allocationRows.map((item) => (
                      <MiniLedgerRow
                        key={item.id}
                        title={`${item.receivableConcept || "Cuenta por cobrar"} · ${formatFinanceMoney(item.amount || 0)}`}
                        subtitle={`${translatePaymentMethod(item.paymentMethod)} · ${formatDateTime(item.createdAt)}`}
                        rightContent={
                          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <StatusPill tone={paymentStatusTone(item.paymentStatus)}>
                              {translatePaymentStatus(item.paymentStatus)}
                            </StatusPill>

                            <MiniActionButton
                              tone="danger"
                              disabled={reversingAllocation || isReadOnly}
                              onClick={() => reverseAllocation(item.id)}
                            >
                              Revertir
                            </MiniActionButton>
                          </div>
                        }
                      />
                    ))}
                  </div>
                )}
              </SubsectionPanel>
            </div>
          </div>
        )}
      </SectionCard>

      {shouldShowFinancialTimeline && (
        <div ref={timelineFinanceRef}>
          <SectionCard
            title="Bitácora financiera"
            subtitle="Trazabilidad completa de movimientos"
          >
            {loadingFinancialTimeline ? (
              <EmptyStateBlock
                title="Cargando bitácora financiera"
                text="Estamos preparando la trazabilidad de movimientos."
                compact
              />
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {displayFinancialTimeline.map((item) => (
                  <LedgerCard
                    key={item.id}
                    title={item.title}
                    eyebrow={formatDateTime(item.occurredAt)}
                    subtitle={item.subtitle || item.type}
                    meta={item.description || ""}
                    status={
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        {item.amount != null ? (
                          <StatusPill tone="neutral">
                            Monto: {formatFinanceMoney(item.amount || 0)}
                          </StatusPill>
                        ) : null}

                        {item.balance != null ? (
                          <StatusPill tone="info">
                            Saldo: {formatFinanceMoney(item.balance || 0)}
                          </StatusPill>
                        ) : null}

                        {item.status ? (
                          <StatusPill tone={paymentStatusTone(item.status)}>
                            {translatePaymentStatus(item.status)}
                          </StatusPill>
                        ) : null}
                      </div>
                    }
                  />
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      )}
    </div>
  )
}

function formatFinanceMoney(value) {
  const parsed = Number(value)
  const safe = Number.isFinite(parsed) ? parsed : 0

  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(safe)
}

function InfoCallout({ title, text }) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 14,
        background: "#eff6ff",
        border: "1px solid #bfdbfe",
        color: "#1e3a8a",
        fontSize: 12.5,
        lineHeight: 1.45,
        fontWeight: 750
      }}
    >
      <div style={{ fontWeight: 950, marginBottom: 3, color: "#1d4ed8" }}>
        {title}
      </div>
      <div>{text}</div>
    </div>
  )
}

function NarrativeBanner({ text }) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 14,
        background:
          "linear-gradient(180deg, rgba(239,246,255,0.95) 0%, rgba(248,250,252,0.96) 100%)",
        border: "1px solid #dbeafe",
        color: "#1e293b",
        fontSize: 13,
        lineHeight: 1.45,
        fontWeight: 700
      }}
    >
      {text}
    </div>
  )
}

function FinanceMetricCard({ title, value, tone = "neutral" }) {
  const visual =
    tone === "danger"
      ? { bg: "#fff7ed", border: "#fed7aa", color: "#c2410c" }
      : tone === "success"
        ? { bg: "#f0fdf4", border: "#bbf7d0", color: "#166534" }
        : tone === "info"
          ? { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" }
          : { bg: "#ffffff", border: "#e2e8f0", color: "#0f172a" }

  return (
    <div
      style={{
        borderRadius: 18,
        padding: 16,
        background: visual.bg,
        border: `1px solid ${visual.border}`,
        boxShadow: "0 6px 18px rgba(15,23,42,0.04)"
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: "#64748b",
          fontWeight: 800,
          marginBottom: 8,
          textTransform: "uppercase",
          letterSpacing: 0.3
        }}
      >
        {title}
      </div>

      <div
        style={{
          fontSize: 28,
          fontWeight: 900,
          color: visual.color,
          lineHeight: 1.05,
          letterSpacing: "-0.03em"
        }}
      >
        {value}
      </div>
    </div>
  )
}

function CompactMetricTile({ label, value }) {
  return (
    <div
      style={{
        ...ui.softCard,
        padding: 12,
        display: "grid",
        gap: 4,
        alignContent: "start"
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 800,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: 0.3
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 22,
          lineHeight: 1.05,
          fontWeight: 900,
          color: "#0f172a"
        }}
      >
        {value}
      </div>
    </div>
  )
}

function SubsectionPanel({ title, subtitle, children }) {
  return (
    <div style={panelBoxStyle}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 900, fontSize: 15, color: "#0f172a" }}>
          {title}
        </div>
        {subtitle ? (
          <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 2 }}>
            {subtitle}
          </div>
        ) : null}
      </div>

      {children}
    </div>
  )
}

function LedgerCard({
  title,
  eyebrow,
  subtitle,
  meta,
  status,
  actions
}) {
  return (
    <div
      style={{
        borderRadius: 16,
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        padding: 14,
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        display: "grid",
        gap: 8
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "flex-start",
          flexWrap: "wrap"
        }}
      >
        <div style={{ display: "grid", gap: 4, minWidth: 0, flex: 1 }}>
          {eyebrow ? (
            <div
              style={{
                fontSize: 11.5,
                fontWeight: 800,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: 0.3
              }}
            >
              {eyebrow}
            </div>
          ) : null}

          <div
            style={{
              fontWeight: 900,
              fontSize: 16,
              lineHeight: 1.25,
              color: "#0f172a",
              wordBreak: "break-word"
            }}
          >
            {title}
          </div>

          {subtitle ? (
            <div
              style={{
                fontSize: 13,
                color: "#475569",
                lineHeight: 1.4
              }}
            >
              {subtitle}
            </div>
          ) : null}
        </div>

        {status ? <div>{status}</div> : null}
      </div>

      {meta ? (
        <div
          style={{
            fontSize: 12.5,
            color: "#64748b",
            lineHeight: 1.4
          }}
        >
          {meta}
        </div>
      ) : null}

      {actions ? <div>{actions}</div> : null}
    </div>
  )
}

function MiniLedgerRow({ title, subtitle, meta, rightContent }) {
  return (
    <div
      style={{
        ...ui.softCard,
        padding: 12,
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        alignItems: "center",
        flexWrap: "wrap"
      }}
    >
      <div style={{ display: "grid", gap: 4, minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontWeight: 800,
            fontSize: 14,
            color: "#0f172a",
            lineHeight: 1.25,
            wordBreak: "break-word"
          }}
        >
          {title}
        </div>

        {subtitle ? (
          <div style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.4 }}>
            {subtitle}
          </div>
        ) : null}

        {meta ? (
          <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.35 }}>
            {meta}
          </div>
        ) : null}
      </div>

      {rightContent ? <div>{rightContent}</div> : null}
    </div>
  )
}

function ActionButton({ tone = "neutral", disabled, onClick, children }) {
  const visual =
    tone === "success"
      ? { bg: "#dcfce7", border: "#bbf7d0", color: "#166534" }
      : tone === "info"
        ? { bg: "#dbeafe", border: "#bfdbfe", color: "#1d4ed8" }
        : { bg: "#ffffff", border: "#cbd5e1", color: "#334155" }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...ui.secondaryButton,
        background: visual.bg,
        border: `1px solid ${visual.border}`,
        color: visual.color,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.7 : 1
      }}
    >
      {children}
    </button>
  )
}

function MiniActionButton({ tone = "neutral", disabled, onClick, children }) {
  const visual =
    tone === "danger"
      ? { bg: "#fee2e2", border: "#fecaca", color: "#991b1b" }
      : tone === "info"
        ? { bg: "#dbeafe", border: "#bfdbfe", color: "#1d4ed8" }
        : { bg: "#ffffff", border: "#cbd5e1", color: "#334155" }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...ui.secondaryButton,
        minHeight: 30,
        padding: "0 10px",
        background: visual.bg,
        border: `1px solid ${visual.border}`,
        color: visual.color,
        fontSize: 11,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.7 : 1
      }}
    >
      {children}
    </button>
  )
}

const panelBoxStyle = {
  borderRadius: 18,
  border: "1px solid #e2e8f0",
  background: "#ffffff",
  boxShadow: "0 8px 18px rgba(15,23,42,0.04)",
  padding: 16
}

const softInnerBoxStyle = {
  ...ui.softCard,
  padding: 14,
  display: "grid",
  gap: 12
}

const checkboxRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 14,
  fontWeight: 700,
  color: "#334155"
}

function buildFinanceNarrative({ overview, openCreditsRows, pendingReceivablesRows }) {
  if (overview.pending > 0 && overview.credit > 0) {
    return `El paciente tiene $${formatPlainMoney(overview.pending)} pendientes y además $${formatPlainMoney(overview.credit)} a favor. Conviene revisar si parte del saldo puede aplicarse antes de cobrar de nuevo.`
  }

  if (overview.pending > 0) {
    return `Hay cartera pendiente por $${formatPlainMoney(overview.pending)}. Este es el foco financiero principal del paciente en este momento.`
  }

  if (overview.credit > 0) {
    return `El paciente mantiene saldo a favor por $${formatPlainMoney(overview.credit)}. Conviene tomarlo en cuenta antes del siguiente cobro.`
  }

  if (openCreditsRows.length || pendingReceivablesRows.length) {
    return "La cuenta está estable, pero todavía hay movimientos financieros que conviene revisar antes de cerrar la lectura."
  }

  return "La cuenta del paciente luce limpia por ahora. Puedes registrar nuevos pagos o revisar la trazabilidad sin ruido adicional."
}

function formatPlainMoney(value) {
  const parsed = Number(value)
  const safe = Number.isFinite(parsed) ? parsed : 0

  return new Intl.NumberFormat("es-MX", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(safe)
}

function formatCount(value) {
  const parsed = Number(value)
  const safe = Number.isFinite(parsed) ? parsed : 0

  return new Intl.NumberFormat("es-MX", {
    maximumFractionDigits: 0
  }).format(safe)
}

function paymentStatusTone(status) {
  const raw = String(status || "").trim().toUpperCase()
  if (raw === "CANCELLED" || raw === "CANCELADO") return "danger"
  if (raw === "POSTED" || raw === "REGISTRADO") return "success"
  if (raw === "PARTIAL" || raw === "PARCIAL") return "warning"
  return "neutral"
}

function receivableStatusTone(status) {
  const raw = String(status || "").trim().toUpperCase()
  if (raw === "PENDING" || raw === "PENDIENTE") return "warning"
  if (raw === "PARTIAL" || raw === "PARCIAL") return "info"
  if (raw === "PAID" || raw === "PAGADO") return "success"
  if (raw === "CANCELLED" || raw === "CANCELADO") return "danger"
  return "neutral"
}

function translatePaymentStatus(status) {
  const raw = String(status || "").trim().toUpperCase()
  if (raw === "POSTED") return "Registrado"
  if (raw === "PARTIAL") return "Parcial"
  if (raw === "CANCELLED") return "Cancelado"
  if (!raw) return "-"
  return raw
}

function translateReceivableStatus(status) {
  const raw = String(status || "").trim().toUpperCase()
  if (raw === "PENDING") return "Pendiente"
  if (raw === "PARTIAL") return "Parcial"
  if (raw === "PAID") return "Pagado"
  if (raw === "CANCELLED") return "Cancelado"
  if (!raw) return "-"
  return raw
}

function translatePaymentMethod(method) {
  const raw = String(method || "").trim().toUpperCase()
  if (raw === "CASH") return "Efectivo"
  if (raw === "CARD") return "Tarjeta"
  if (raw === "TRANSFER") return "Transferencia"
  if (raw === "MIXED") return "Mixto"
  if (raw === "OTHER") return "Otro"
  if (!raw) return "-"
  return raw
}

function translateReferenceType(referenceType) {
  const raw = String(referenceType || "").trim().toUpperCase()
  if (raw === "APPOINTMENT") return "Cita"
  if (raw === "MANUAL") return "Manual"
  if (raw === "TREATMENT") return "Tratamiento"
  if (!raw) return "-"
  return raw
}